import React, { FormEvent } from 'react'
import { useForm, Link } from '@inertiajs/react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import FormHelperText from '@mui/material/FormHelperText'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface Agent {
  id: number
  username: string
}

interface ProjectRef {
  id: number
  name: string
}

interface Props {
  agents: Agent[]
  projects: ProjectRef[]
}

export default function New({ agents, projects }: Props) {
  const { data, setData, post, processing, errors } = useForm({
    webhook: {
      name: '',
      description: '',
      responsible_agent_id: '' as string | number,
      project_id: projects.length === 1 ? projects[0].id : '' as any,
      active: true,
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post('/manage/webhooks/webhooks')
  }

  return (
    <SidebarLayout section="manage" title="New webhook">
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 600,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {projects.length > 1 ? (
            <FormControl fullWidth sx={{ mb: 2.5 }} error={'webhook.project_id' in errors}>
              <InputLabel>Project</InputLabel>
              <Select
                value={data.webhook.project_id}
                label="Project"
                onChange={e => setData('webhook', { ...data.webhook, project_id: e.target.value as number })}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
              {('webhook.project_id' in errors) && (
                <FormHelperText>{(errors as Record<string, string>)['webhook.project_id']}</FormHelperText>
              )}
            </FormControl>
          ) : projects.length === 1 ? (
            <TextField
              label="Project"
              fullWidth
              value={projects[0].name}
              disabled
              sx={{ mb: 2.5 }}
            />
          ) : null}
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={data.webhook.name}
            onChange={e => setData('webhook', { ...data.webhook, name: e.target.value })}
            error={'webhook.name' in errors}
            helperText={(errors as Record<string, string>)['webhook.name']}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={3}
            value={data.webhook.description}
            onChange={e => setData('webhook', { ...data.webhook, description: e.target.value })}
            error={'webhook.description' in errors}
            helperText={(errors as Record<string, string>)['webhook.description'] || 'Optional description of what this webhook receives'}
            sx={{ mb: 2.5 }}
          />
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'webhook.responsible_agent_id' in errors}>
            <InputLabel>Responsible agent</InputLabel>
            <Select
              value={data.webhook.responsible_agent_id}
              label="Responsible agent"
              onChange={e => setData('webhook', { ...data.webhook, responsible_agent_id: e.target.value as number })}
            >
              {agents.map(agent => (
                <MenuItem key={agent.id} value={agent.id}>{agent.username}</MenuItem>
              ))}
            </Select>
            {'webhook.responsible_agent_id' in errors ? (
              <FormHelperText>{(errors as Record<string, string>)['webhook.responsible_agent_id']}</FormHelperText>
            ) : (
              <FormHelperText>The agent that will handle incoming events</FormHelperText>
            )}
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.webhook.active}
                onChange={e => setData('webhook', { ...data.webhook, active: e.target.checked })}
              />
            }
            label="Active"
            sx={{ mb: 2.5 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Create webhook
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href="/manage/webhooks/webhooks" style={{ color: '#1a1a1a' }}>Back to webhooks</Link>
          </Box>
        </Box>
      </Paper>
    </SidebarLayout>
  )
}
